<script setup lang="ts">
import { api } from '@/api';
import { EntityWithCommentType, ReferencingObjectDto } from '@/api/generator';
import Comments from '@/components/comments/Comments.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import HistoryEvents from '@/components/history/HistoryEvents.vue';
import BasePageNavigation from '@/components/pages/BasePage/BasePageNavigation.vue';
import { defaultPageTabs, getDefaultPageActions } from '@/components/pages/BasePage/defaults';
import type { PageAction } from '@/components/pages/BasePage/types';
import { TabDefinition } from '@/components/Tabs/types.ts';
import { routenames } from '@/router/routenames';
import { useUnitsStore } from '@/stores/unitsStore';
import { isPredefinedEntity, translateEntityProperty } from '@/utils/helper.ts';
import { useTranslation } from 'i18next-vue';
import { useFormValues } from 'vee-validate';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Unit } from '../types';
import { useDeleteUnitWithConfirmation } from '../utils';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import EntityReferencesModal from '@/components/EntityReferencesModal/EntityReferencesModal.vue';
import { useAmountComments } from '@/components/comments/useAmountComments';
import { useFormTab } from '@/components/forms/useFormTab';

const { t } = useTranslation();
const router = useRouter();
const values = useFormValues<Unit>();
const mode = useFormMode();

const unitsStore = useUnitsStore();

const { action: deleteAction } = useDeleteUnitWithConfirmation(unitsStore.deleteUnit);
const entitiesDeleted = ref(false);
const entityReferences = ref<ReferencingObjectDto[] | null>(null);

const { commentsChanged, commentsTabTitle } = useAmountComments(values);

const { getIndicatorState } = useFormTab();

const tabs = computed(
  () =>
    ({
      administrative: {
        ...defaultPageTabs.administrative,
        indicatorState: getIndicatorState(['name', 'abbreviation', 'attachments']),
      },

      comments: {
        ...defaultPageTabs.comments,
        hidden: mode.value === 'add',
        title: commentsTabTitle.value,
      },
      history: {
        ...defaultPageTabs.history,
        hidden: defaultPageTabs.history.hidden || mode.value === 'add',
        unmountTab: true,
      },
    }) satisfies TabDefinition,
);

const actions = computed<PageAction[]>(() => {
  const defaultPageActions = getDefaultPageActions(mode.value, values.value);

  return [
    {
      ...defaultPageActions.edit,
      to: {
        name: routenames.units.edit,
        params: { id: values.value.id },
      },
    },
    {
      ...defaultPageActions.delete,
      callback: async () => {
        if (values.value.id) {
          const { result } = await deleteAction({
            param: values.value.id,
            confirmationOptions: getDefaultDeleteConfirmationOptions(
              {
                ...(values.value as EntityInfo),
                typeName: 'Unit_one',
              },
              t,
            ),
          });

          if (result?.success) {
            entitiesDeleted.value = true;
            router.push({ name: routenames.units.overview });
          }
        }
      },
    },
  ];
});

const symbolInfoMessage = () => {
  if (mode.value !== 'details') {
    return values.value.isReferenced
      ? t('UnitSymbolInfoDisabledMessage')
      : t('UnitSymbolInfoMessage');
  }
  return undefined;
};

const isInEditMode = mode.value === 'edit';
const isReferenced = computed(() => values.value.isReferenced ?? false);
const isPredefined = computed(() => isPredefinedEntity(values.value));
</script>

<template>
  <BasePageNavigation :tabs="tabs" :actions="actions">
    <template #administrative>
      <InlineNotification
        v-if="(isReferenced || isPredefined) && isInEditMode"
        variant="flat"
        class="mb-4"
        :value="{
          type: 'info',
          message: $t('FieldsCanNotBeEditedBecauseOfReferenceOfEntityMsg', {
            entity: $t('Unit_one'),
          }),
        }"
      >
        <template #append>
          <span
            v-if="isReferenced"
            class="ml-2 linkButton"
            @click.stop="() => (entityReferences = values.isReferencedBy ?? null)"
            >{{ $t('Info') }}</span
          >
          <EntityReferencesModal
            v-if="isReferenced"
            :references="entityReferences"
            :title="$t('ReferenceingEntities')"
            @dialogOpen="
              (isOpen: boolean) => {
                if (!isOpen) entityReferences = null;
              }
            "
          ></EntityReferencesModal>
        </template>
      </InlineNotification>
      <FormSection>
        <v-row class="justify-center justify-sm-space-between">
          <v-col cols="8">
            <v-row>
              <v-col cols="4">
                <VeeTextField
                  :label="$t('Name')"
                  name="name"
                  :placeholder="$t('Required')"
                  :outputConverter="() => translateEntityProperty('name', values)"
                />
              </v-col>

              <v-col cols="3">
                <VeeTextField
                  :label="$t('UnitSymbol')"
                  :disabled="mode === 'edit' && values.isReferenced"
                  name="abbreviation"
                  :infoMessage="symbolInfoMessage()"
                  :placeholder="$t('Required')"
                />
              </v-col>

              <v-col cols="5">
                <VeeTextarea
                  name="description"
                  :label="$t('Description')"
                  :outputConverter="() => translateEntityProperty('description', values)"
                  rows="7"
                />
                <VeeAttachments
                  class="mt-1"
                  name="attachments"
                  data-testid="Description_Attachments"
                ></VeeAttachments>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="auto">
            <VeeAvailability :disabled="mode !== 'details'" name="isAvailable" />
            <EntityChangeBlock v-if="mode === 'details'" :entity="values" />
          </v-col>
        </v-row>
      </FormSection>
    </template>

    <template #comments>
      <FormSection>
        <Comments
          v-if="values.id"
          :id="values.id"
          :entityWithCommentType="EntityWithCommentType.Unit"
          @change="commentsChanged"
        />
      </FormSection>
    </template>

    <template #history>
      <HistoryEvents
        v-if="values.id"
        :id="values.id"
        :historyApiFn="api.unitsApi.getUnitHistory.bind(api.unitsApi)"
      />
    </template>
  </BasePageNavigation>
</template>
